/**
 * Exclusive Elite Plans family member surface (mirrors `/elite-plans-member`).
 *
 * Belongs here:
 * - Elite tab → Active Elite dashboard (secondary Elite, or both Elite)
 * - Prime tab → Active Prime dashboard when secondary already has Prime
 * - Prime tab → FamilyPurchaseBody when secondary is free (family Continue)
 * - Prime tab → SellBody when secondary is Elite (Upgrade to Prime) or primary is blocked
 * Does not belong here: both-free blocked landing (`/elite-plans-family-landing`).
 */
import '../elitePlansTheme.css'
import {
  ELITE_PLANS_ELITE_TAB_ID,
  ElitePlansLandingProvider,
  useElitePlansLanding
} from '../ElitePlansLandingContext'
import { ElitePlansAnalyticsScreen } from '../constants/analyticsEvents'
import { ELITE_PLANS_FAMILY_MEMBER_STATUS_QUERY_KEY } from '../constants/queryKeys'
import { ELITE_PRIME_DASHBOARD_FEATURES_ELITE_BUNDLE_LABEL } from '../constants/copy'
import {
  resolveIsFamilyPrimePurchaseForSecondary,
  resolveIsFamilySecondaryPrime
} from '../elitePlansMembership'
import { EliteBuyCtaExtrasProvider } from '../../../components/elite/EliteBuyCtaExtrasContext'
import { ElitePlansEliteMemberDashboard } from '../components/ElitePlansEliteMemberDashboard'
import { ElitePlansFamilyPurchaseBody } from '../components/ElitePlansFamilyPurchaseBody'
import { ElitePlansSellBody } from '../components/ElitePlansSellBody'
import { ElitePlansTabbedPageLayout } from '../components/ElitePlansTabbedPageLayout'
import { ElitePlansPageShell } from '../components/ElitePlansPageShell'
import { ElitePlansHeader } from '../components/ElitePlansHeader'
import { ElitePlansTabPanel } from '../components/ElitePlansTabPanel'
import { ElitePrimeDashboardHero } from '../components/ElitePrimeDashboardHero'
import { ElitePlansDashboardBelowFold } from '../components/ElitePlansDashboardBelowFold'
import { ElitePlansHeroStatusSkeleton } from '../components/hero/ElitePlansHeroStatusSkeleton'
import { NewEliteLandingAnalyticsScreen } from '../../new-elite-landing/analytics/newEliteLandingAnalytics'
import { useTrackElitePlansScreenLoadWhenReady } from '../hooks/useTrackElitePlansScreenLoadWhenReady'

export default function ElitePlansFamilyMemberPage() {
  return (
    <ElitePlansLandingProvider
      analyticsScreen={ElitePlansAnalyticsScreen.FamilyMember}
      statusQueryKeyRoot={ELITE_PLANS_FAMILY_MEMBER_STATUS_QUERY_KEY}
    >
      <ElitePlansFamilyMemberPageContent />
    </ElitePlansLandingProvider>
  )
}

function ElitePlansFamilyMemberPageContent() {
  const { selectedPlanId, status, isStatusLoading } = useElitePlansLanding()
  const showEliteDashboard = selectedPlanId === ELITE_PLANS_ELITE_TAB_ID

  // Elite dashboard fires ScreenLoaded when its own advisory status is ready
  useTrackElitePlansScreenLoadWhenReady(!showEliteDashboard && !isStatusLoading)

  if (showEliteDashboard) {
    return (
      <ElitePlansTabbedPageLayout>
        <ElitePlansEliteMemberDashboard
          statusQueryKeyRoot={ELITE_PLANS_FAMILY_MEMBER_STATUS_QUERY_KEY}
          analyticsScreen={NewEliteLandingAnalyticsScreen.FamilyDashboard}
        />
      </ElitePlansTabbedPageLayout>
    )
  }

  // Wait for status on Prime tab — do not flash SellBody before FamilyPurchase / dashboard
  if (isStatusLoading) {
    return (
      <ElitePlansPageShell isContentScrollOwner bottomInsetPx={24}>
        <ElitePlansHeader />
        <ElitePlansTabPanel>
          <ElitePlansHeroStatusSkeleton showTenure showPlusBanner />
        </ElitePlansTabPanel>
      </ElitePlansPageShell>
    )
  }

  // Secondary already Prime → Active Prime dashboard inside Elite/Prime tabs
  if (resolveIsFamilySecondaryPrime(status)) {
    return (
      <ElitePlansTabbedPageLayout>
        <FamilyMemberPrimeDashboardBody />
      </ElitePlansTabbedPageLayout>
    )
  }

  // Free secondary → family Continue; Elite secondary falls through to SellBody Upgrade
  if (resolveIsFamilyPrimePurchaseForSecondary(status)) {
    return (
      <EliteBuyCtaExtrasProvider status={status}>
        <ElitePlansPageShell isContentScrollOwner bottomInsetPx={24}>
          <ElitePlansHeader />
          <ElitePlansTabPanel>
            <ElitePlansFamilyPurchaseBody />
          </ElitePlansTabPanel>
        </ElitePlansPageShell>
      </EliteBuyCtaExtrasProvider>
    )
  }

  // Primary free → blocked Upgrade; primary Elite (incl. both-Elite) → Upgrade/Get Prime sell
  return (
    <ElitePlansTabbedPageLayout>
      <ElitePlansSellBody />
    </ElitePlansTabbedPageLayout>
  )
}

/** Active Prime hero + below-fold for the family-member Prime tab (tabs already in layout). */
function FamilyMemberPrimeDashboardBody() {
  const { isStatusLoading } = useElitePlansLanding()
  return (
    <>
      <ElitePrimeDashboardHero />
      <ElitePlansDashboardBelowFold
        isContentLoading={isStatusLoading}
        eliteBundleLabel={ELITE_PRIME_DASHBOARD_FEATURES_ELITE_BUNDLE_LABEL}
      />
    </>
  )
}
